import { supabase } from '../../shared/supabase/supabaseClient';
import { buildSupportTicketContext } from '../../shell/support/supportTicketContext';
import { insertSupportTicket } from '../../shell/support/services/supportTicketsRepo';
import {
  otherRubroTemplateLabel,
  type OtherRubroOperationTemplate,
  type OtherRubroSupportSource,
} from './otherRubroFlow';

export type CreateOtherRubroSupportTicketInput = {
  organizationId: string;
  branchId: string | null;
  userId: string;
  source: OtherRubroSupportSource;
  requestedVerticalLabel: string;
  operationTemplate: OtherRubroOperationTemplate;
  resolvedBusinessTypeCode: string;
  pickerBusinessTypeCode?: string;
  selectedFeatureIds?: readonly string[];
};

async function hasRecentSupportTicket(
  organizationId: string,
  source: string,
  hours = 24,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_recent_support_ticket', {
    p_organization_id: organizationId,
    p_source: source,
    p_hours: hours,
  });
  if (error) {
    // ponytail: fail-safe dedupe — si no podemos verificar, no creamos ticket (evita duplicados)
    console.error('[otherRubroSupportTicket] has_recent_support_ticket RPC error', error);
    return true;
  }
  return Boolean(data);
}

/**
 * Ticket automático al elegir Otros. Dedupe 24h por org + source; no bloquea el flujo.
 */
export async function createOtherRubroSupportTicketIfNeeded(
  input: CreateOtherRubroSupportTicketInput,
): Promise<{ created: boolean; ticketId?: string }> {
  const label = input.requestedVerticalLabel.trim();
  if (!label) return { created: false };

  const duplicate = await hasRecentSupportTicket(input.organizationId, input.source);
  if (duplicate) {
    console.info('[otherRubroSupportTicket] skip duplicate', {
      organizationId: input.organizationId,
      source: input.source,
    });
    return { created: false };
  }

  const templateLabel = otherRubroTemplateLabel(input.operationTemplate);
  const featuresLine =
    input.selectedFeatureIds && input.selectedFeatureIds.length > 0
      ? `Features: ${input.selectedFeatureIds.join(', ')}`
      : null;
  const body = [
    `Rubro no catalogado: ${label}`,
    `Plantilla: ${templateLabel}`,
    featuresLine,
    `ERP asignado: ${input.resolvedBusinessTypeCode}`,
  ]
    .filter(Boolean)
    .join('\n');

  const baseContext = await buildSupportTicketContext({
    branchId: input.branchId,
    branchName: null,
  });

  const { id } = await insertSupportTicket({
    organizationId: input.organizationId,
    branchId: input.branchId,
    userId: input.userId,
    ticketType: 'improvement',
    body,
    context: {
      ...baseContext,
      source: input.source,
      requested_vertical_label: label,
      operation_template: input.operationTemplate,
      selected_feature_ids: input.selectedFeatureIds ?? [],
      resolved_business_type_code: input.resolvedBusinessTypeCode,
      picker_business_type_code: input.pickerBusinessTypeCode ?? 'general_retail',
    },
  });

  return { created: true, ticketId: id };
}

export async function hasRecentCatalogCategoryRequest(
  organizationId: string,
  categoryName: string,
  days = 7,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('ticket_type', 'improvement')
    .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
    .contains('context', { source: 'catalog_category_request' })
    .ilike('context->>requested_category_name', categoryName.trim())
    .limit(1);

  if (error) {
    console.warn('[otherRubroSupportTicket] category dedupe check', error);
    return false;
  }
  return (data?.length ?? 0) > 0;
}
